import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { AiOutlineFileImage, AiOutlineArrowLeft, AiOutlineEye } from 'react-icons/ai';
import Header from "../../../components/protected/Header";
import { useTranslations } from '../../../translations';
import { useLocation, useNavigate } from 'react-router-dom';

const bloomsColorMap = {
  crear: "#e74c3c",
  evaluar: "#e67e22",
  analizar: "#f1c40f",
  aplicar: "#2ecc71",
  entender: "#3498db",
  recordar: "#9b59b6",
};

const difficultyColorMap = {
  "fácil": "#2ecc71",
  "medio": "#f1c40f",
  "difícil": "#e74c3c",
  "easy": "#2ecc71",
  "medium": "#f1c40f",
  "hard": "#e74c3c"
};

const InferenceView = () => {
  const { t } = useTranslations();
  const location = useLocation();
  const navigate = useNavigate();
  
  const lesson = location.state?.lesson;

  // If no lesson is passed, fallback
  if (!lesson) {
    return (
      <div className="dashboard-wrapper">
        <Header />
        <Container className="text-center mt-5">
           <h4>{t("lessonNotFound")}</h4>
           <Button onClick={() => navigate('/inference')}>{t("back")}</Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid>
          <div className="mb-4">
            <Button 
               variant="link" 
               className="text-decoration-none text-plum fw-bold d-inline-flex align-items-center gap-2 p-0 mb-3"
               onClick={() => navigate('/inference')}
            >
              <AiOutlineArrowLeft /> {t("back") || "Volver al listado"}
            </Button>
            <h1 className="dashboard-title animate__animated animate__fadeInDown text-plum fs-2 fw-bold">
               {lesson.idea}
            </h1>
            {/* Difficulty / Levels info */}
            {(lesson.difficulty || (lesson.levels && lesson.levels.length > 0)) && (
              <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                {lesson.difficulty && (
                  <span className="badge rounded-pill px-3 py-2 bg-light text-dark border fw-bold small text-uppercase">
                    {t("difficulty")}: {lesson.difficulty}
                  </span>
                )}
                {lesson.levels && lesson.levels.length > 0 && lesson.levels.map((lvl, i) => (
                  <span 
                    key={i}
                    className="badge rounded-pill px-3 py-1 fw-bold text-white" 
                    style={{ 
                      backgroundColor: bloomsColorMap[lvl?.toLowerCase()] || 'var(--plum-bg)',
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {lvl}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="animate__animated animate__fadeInUp">
            <Row className="g-4">
              {/* Image Section */}
              <Col lg={5} className="d-flex align-items-start">
                <div className="modern-card p-0 shadow-sm w-100 overflow-hidden" style={{ borderRadius: '15px', border: 'none', backgroundColor: 'var(--plum-bg)', height: 'fit-content' }}>
                  <div className="p-3 text-white text-center d-flex align-items-center justify-content-center gap-2 fw-bold" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                    <AiOutlineFileImage size={24} /> {t("inferenceLearning") || "Imagen Generada"}
                  </div>
                  <div className="p-0 position-relative w-100" style={{ backgroundColor: '#fff' }}>
                     <img 
                        src={lesson.imageUrl} 
                        alt={lesson.idea} 
                        className="img-fluid w-100" 
                        style={{ objectFit: 'contain' }}
                     />
                  </div>
                </div>
              </Col>

              {/* Content Section */}
              <Col lg={7}>
                <div className="modern-card p-4 shadow-sm h-100 mb-0 d-flex flex-column gap-4" style={{ borderRadius: '15px', backgroundColor: 'white' }}>
                  
                  {/* Reading Text */}
                  {lesson.readingText && (
                    <div className="p-4 shadow-sm" style={{ backgroundColor: '#f8f9fa', borderRadius: '15px', borderLeft: '5px solid var(--plum-bg)' }}>
                      <h4 className="fw-bold color-plum mb-3 fs-5 d-flex align-items-center gap-2">
                        <AiOutlineFileImage size={24} className="text-muted" /> {t("inferenceReadingText") || "Texto para Lectura"}
                      </h4>
                      <div className="text-muted" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                        {lesson.readingText}
                      </div>
                    </div>
                  )}

                  {/* Vocabulary List */}
                  {lesson.vocabulary && lesson.vocabulary.length > 0 && (
                    <div className="p-3" style={{ backgroundColor: 'rgba(235, 33, 120, 0.05)', borderRadius: '12px' }}>
                      <h4 className="fw-bold color-plum mb-3 fs-5">{t("vocabularyList") || "Vocabulario"}</h4>
                      <div className="d-flex flex-wrap gap-2">
                         {lesson.vocabulary.map((voc, idx) => (
                           <div key={idx} className="bg-white px-3 py-2 rounded-pill shadow-sm border border-light d-flex align-items-center gap-2">
                             <span className="fw-bold" style={{ color: 'var(--pink-bright)' }}>{voc.word}:</span>
                             <span className="text-muted small">{voc.definition}</span>
                           </div>
                         ))}
                      </div>
                    </div>
                  )}

                  {/* Questions */}
                  {lesson.questions && lesson.questions.length > 0 && (
                    <div>
                      <h4 className="fw-bold color-plum mb-3 fs-5">{t("inferenceQuestions") || "Preguntas de Inferencia"}</h4>
                      <div className="d-flex flex-column gap-3">
                         {lesson.questions.map((q, qIdx) => (
                           <Card key={qIdx} className="border-0 shadow-sm rounded-4 overflow-hidden">
                             <Card.Header className="bg-light border-0 pt-3 pb-2 px-4 d-flex justify-content-between align-items-center">
                                <span className="fw-bold fs-6" style={{ color: 'var(--plum-bg)' }}>
                                  {qIdx + 1}. {q.question}
                                </span>
                                {q.level && (
                                  <span 
                                    className="badge rounded-pill px-3 py-1 fw-bold text-white" 
                                    style={{ 
                                      backgroundColor: bloomsColorMap[q.level?.toLowerCase()] || 'var(--plum-bg)',
                                      fontSize: '0.7rem',
                                      letterSpacing: '0.5px',
                                      textTransform: 'uppercase',
                                    }}
                                  >
                                    {q.level}
                                  </span>
                                )}
                             </Card.Header>
                             <Card.Body className="pt-2 pb-3 px-4">
                                <ul className="list-unstyled mb-0 d-flex flex-column gap-2 mt-2">
                                   {q.options && q.options.map((opt, oIdx) => (
                                      <li 
                                        key={oIdx} 
                                        className={`px-3 py-2 rounded-3 small ${oIdx === q.correctOptionIndex ? 'fw-bold' : 'text-muted'}`}
                                        style={{ 
                                          backgroundColor: oIdx === q.correctOptionIndex ? 'rgba(40, 167, 69, 0.1)' : 'rgba(0,0,0,0.02)',
                                          color: oIdx === q.correctOptionIndex ? '#28a745' : 'inherit',
                                          border: oIdx === q.correctOptionIndex ? '1px solid rgba(40, 167, 69, 0.2)' : '1px solid transparent'
                                        }}
                                      >
                                         <span className="me-2">{String.fromCharCode(65 + oIdx)}.</span> {opt} {oIdx === q.correctOptionIndex && '✓'}
                                      </li>
                                   ))}
                                </ul>
                             </Card.Body>
                           </Card>
                         ))}
                      </div>
                    </div>
                  )}

                  {/* Image Questions */}
                  {lesson.imageQuestions && lesson.imageQuestions.length > 0 && (
                    <div className="mt-2">
                      <h4 className="fw-bold mb-3 fs-5" style={{ color: 'var(--pink-bright)' }}>
                        <AiOutlineEye className="me-2" size={24} /> 
                        {t("imageQuestionsTitle") || "Preguntas de Observación Visual"}
                      </h4>
                      <div className="d-flex flex-column gap-3">
                         {lesson.imageQuestions.map((q, qIdx) => (
                           <Card key={`img-q-${qIdx}`} className="border-0 shadow-sm rounded-4 overflow-hidden" style={{ borderLeft: `4px solid ${difficultyColorMap[q.difficulty?.toLowerCase()] || 'var(--pink-bright)'}` }}>
                             <Card.Header className="bg-light border-0 pt-3 pb-2 px-4 d-flex justify-content-between align-items-start gap-3">
                                <span className="fw-bold fs-6 flex-grow-1" style={{ color: 'var(--plum-bg)' }}>
                                  {qIdx + 1}. {q.question}
                                </span>
                                {q.difficulty && (
                                  <span 
                                    className="badge rounded-pill px-3 py-1 fw-bold text-white flex-shrink-0 mt-1" 
                                    style={{ 
                                      backgroundColor: difficultyColorMap[q.difficulty?.toLowerCase()] || 'var(--pink-bright)',
                                      fontSize: '0.7rem',
                                      letterSpacing: '0.5px',
                                      textTransform: 'uppercase',
                                    }}
                                  >
                                    {q.difficulty}
                                  </span>
                                )}
                             </Card.Header>
                             <Card.Body className="pt-2 pb-3 px-4">
                                <ul className="list-unstyled mb-0 d-flex flex-column gap-2 mt-2">
                                   {q.options && q.options.map((opt, oIdx) => (
                                      <li 
                                        key={oIdx} 
                                        className={`px-3 py-2 rounded-3 small ${oIdx === q.correctOptionIndex ? 'fw-bold' : 'text-muted'}`}
                                        style={{ 
                                          backgroundColor: oIdx === q.correctOptionIndex ? 'rgba(40, 167, 69, 0.1)' : 'rgba(0,0,0,0.02)',
                                          color: oIdx === q.correctOptionIndex ? '#28a745' : 'inherit',
                                          border: oIdx === q.correctOptionIndex ? '1px solid rgba(40, 167, 69, 0.2)' : '1px solid transparent'
                                        }}
                                      >
                                         <span className="me-2 fw-bold" style={{ opacity: 0.7 }}>{String.fromCharCode(65 + oIdx)}.</span> 
                                         {opt} 
                                         {oIdx === q.correctOptionIndex && <span className="ms-2">✓</span>}
                                      </li>
                                   ))}
                                </ul>
                             </Card.Body>
                           </Card>
                         ))}
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </main>
    </div>
  );
};

export default InferenceView;
